/**
 * AI Scope Guard
 * Detecta preguntas fuera del alcance deportivo antes de gastar créditos LLM.
 */
import { trackEvent } from "@/lib/audit";

export type ScopeIntent =
  | "match_question"
  | "player_question"
  | "team_question"
  | "tactic_question"
  | "prediction_question"
  | "stats_question"
  | "rewards_question"
  | "app_help_question"
  | "out_of_scope";

const KEYWORDS: Record<Exclude<ScopeIntent, "out_of_scope">, RegExp> = {
  match_question: /\b(partido|match|jogo|score|marcador|resultado|minuto|tiempo|gol|goal|empate|tie|var|tarjeta|card|penal|penalty|corner|c[oó]rner|libre|kick|halftime|fulltime)\b/i,
  player_question: /\b(jugador|jugadores|player|players|jogador|jogadores|messi|lozano|martinez|álvarez|alvarez|ochoa|chicharito|mbapp[eé]|ronaldo|nombre|n[uú]mero|number|posici[oó]n|position|decisiv[oa]s?|top[- ]?performers?|mejor(es)?|melhor(es)?|best|star|estrella|craque|mvp|goleador|scorer|asistencia|assist)\b/i,
  team_question: /\b(m[eé]xico|mexico|argentina|brasil|usa|equipo|team|seleccion|selecci[oó]n|alineaci[oó]n|lineup|formaci[oó]n|formation)\b/i,
  tactic_question: /\b(t[aá]ctica|tactic|tactical|presi[oó]n|pressing|contraataque|counter|posesi[oó]n|possession|momentum|estrategia|strategy|defensa|defense|ataque|attack|formaci[oó]n|formaciones|formations|ajuste|adjust|compar(a|e|ar)|compare|sustitu[ií](r|do|cion|ç[aã]o)?|substitu[ií](r|tion|te|do)?|cambio|change|sub|rotaci[oó]n|rotation|riesg(o|os)|risk|risks)\b/i,
  prediction_question: /\b(predicci[oó]n|prediction|probabilidad|probability|chance|qui[eé]n gana|who wins|remontada|comeback|next goal|pr[oó]ximo gol|mvp|top scorer)\b/i,
  stats_question: /\b(estad[ií]stica|stats|xg|posesi[oó]n|tiros|shots|pases|passes|disparos|porcentaje|percent|hist[oó]rico|history)\b/i,
  rewards_question: /\b(puntos|points|recompensa|reward|cup[oó]n|coupon|premio|prize|nivel|level|fan score|insignia|badge)\b/i,
  app_help_question: /\b(c[oó]mo (uso|funciona)|how (do|does)|qu[eé] es|what is|d[oó]nde|where|configurar|settings|idioma|language|notificaci[oó]n|notification|radar|mini[- ]?map)\b/i,
  // NEW intent — injury / fatigue / substitution reports.
};

// Extend with injury keywords on top of player_question regex via fallback
// pass: any of these triggers force in-scope as tactic_question.
const INJURY_RE = /\b(lesi[oó]n|lesiones|injury|injuries|injured|les[aã]o|les[oõ]es|fatiga|fatigue|fadiga|cansad[oa]s?|tired|exhausted|desgaste|wear|stamina|resistencia|reporte|report|relatorio|relat[oó]rio)\b/i;

export interface ScopeResult {
  inScope: boolean;
  intent: ScopeIntent;
  fallbackReply?: string;
  confidence: number;
}

export function evaluateScope(question: string, lang: "ES" | "EN" | "PT" = "ES"): ScopeResult {
  const q = (question ?? "").trim();
  if (!q) {
    return { inScope: false, intent: "out_of_scope", confidence: 0, fallbackReply: outOfScopeReply(lang) };
  }
  for (const [intent, re] of Object.entries(KEYWORDS) as [Exclude<ScopeIntent, "out_of_scope">, RegExp][]) {
    if (re.test(q)) {
      void trackEvent("ai_oracle_intent_detected", { intent, len: q.length });
      return { inScope: true, intent, confidence: 0.85 };
    }
  }
  // Specialized fallback for injury / fatigue / substitution prompts.
  if (INJURY_RE.test(q)) {
    void trackEvent("ai_oracle_intent_detected", { intent: "tactic_question", len: q.length, source: "injury_fallback" });
    return { inScope: true, intent: "tactic_question", confidence: 0.8 };
  }
  // Heuristic: very short questions often follow context — allow them.
  if (q.length < 12) {
    return { inScope: true, intent: "match_question", confidence: 0.4 };
  }
  void trackEvent("ai_out_of_scope_question", { question: q.slice(0, 120) });
  void trackEvent("ai_scope_guard_triggered", { reason: "no_keyword_match" });
  return {
    inScope: false,
    intent: "out_of_scope",
    confidence: 0.7,
    fallbackReply: outOfScopeReply(lang),
  };
}

export function outOfScopeReply(lang: "ES" | "EN" | "PT"): string {
  switch (lang) {
    case "EN":
      return "I'm specialized in real-time football analysis for this match. Ask me about players, tactics, probabilities, stats or predictions.";
    case "PT":
      return "Sou especializado em análise de futebol em tempo real deste jogo. Pergunte sobre jogadores, táticas, probabilidades, estatísticas ou previsões.";
    default:
      return "Estoy especializado en análisis deportivo y en este partido en tiempo real. Pregúntame sobre jugadores, tácticas, probabilidades, estadísticas o predicciones.";
  }
}
